/**
 * Helper function that casts the mix constant value to a boolean
 */
const envToBoolean = (value?: string): boolean => {
  return value === 'true' || value === '1';
};

/**
 * Algolia
 */
export const ALGOLIA_APP_ID = process.env.MIX_ALGOLIA_APP_ID;
export const ALGOLIA_API_SEARCH_KEY = process.env.MIX_ALGOLIA_ANONYMOUS_USER_SEARCH_KEY;

/**
 * Algolia Indexes
 */
export const ALGOLIA_BREEDER_INDEX = process.env.MIX_ALGOLIA_BREEDER_INDEX_NAME;
export const ALGOLIA_BREED_INDEX = process.env.MIX_ALGOLIA_BREED_INDEX_NAME;
export const ALGOLIA_ALL_LISTINGS_INDEX = process.env.MIX_ALGOLIA_ALL_LISTINGS_INDEX_NAME;

/**
 * CLOUDINARY CLOUD NAMES
 */
export const CLOUDINARY_CLOUD_NAME = process.env.MIX_CLOUDINARY_CLOUD_NAME;

/**
 * CLOUDINARY UPLOAD PRESETS
 */
/* eslint-disable max-len */
export const CLOUDINARY_UPLOAD_PRESET_DEFAULT = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_DEFAULT;
export const CLOUDINARY_UPLOAD_PRESET_LISTING = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_LISTING;
export const CLOUDINARY_UPLOAD_PRESET_PUPPY = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_PUPPY;
export const CLOUDINARY_UPLOAD_PRESET_SELLER = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_SELLER;
export const CLOUDINARY_UPLOAD_PRESET_BREED = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_BREED;
export const CLOUDINARY_UPLOAD_PRESET_FORM_SUBMISSION = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_FORM_SUBMISSION;
export const CLOUDINARY_UPLOAD_PRESET_TESTIMONIAL = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_TESTIMONIAL;
export const CLOUDINARY_UPLOAD_PRESET_BLOG_BANNER = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_BLOG_BANNER;
export const CLOUDINARY_UPLOAD_PRESET_BLOG_CONTENT = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_BLOG_CONTENT;
export const CLOUDINARY_UPLOAD_PRESET_PAGE_BANNER = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_PAGE_BANNER;
export const CLOUDINARY_UPLOAD_PRESET_PAGE_CONTENT = process.env.MIX_CLOUDINARY_UPLOAD_PRESET_PAGE_CONTENT;
/* eslint-enable max-len */

export type CloudinaryUploadPreset =
  typeof CLOUDINARY_UPLOAD_PRESET_DEFAULT |
  typeof CLOUDINARY_UPLOAD_PRESET_LISTING |
  typeof CLOUDINARY_UPLOAD_PRESET_PUPPY |
  typeof CLOUDINARY_UPLOAD_PRESET_SELLER |
  typeof CLOUDINARY_UPLOAD_PRESET_BREED |
  typeof CLOUDINARY_UPLOAD_PRESET_FORM_SUBMISSION |
  typeof CLOUDINARY_UPLOAD_PRESET_TESTIMONIAL |
  typeof CLOUDINARY_UPLOAD_PRESET_BLOG_BANNER |
  typeof CLOUDINARY_UPLOAD_PRESET_BLOG_CONTENT |
  typeof CLOUDINARY_UPLOAD_PRESET_PAGE_BANNER |
  typeof CLOUDINARY_UPLOAD_PRESET_PAGE_CONTENT;

/**
 * Routes
 * NOTE: These should match the routes found in the `routes/` dir
 */

export const BROWSER_API_PREFIX = '/api/browser/v2';

// Pages
export const PAGES_PREFIX = '/pages';
export const PAGES_ADMIN_PREFIX = '/admin/pages';

// Blogs
export const BLOGS_PREFIX = '/blogs';
export const BLOGS_ADMIN_PREFIX = '/admin/blogs';

// Breeds
export const BREEDS_PREFIX = '/breeds/';
export const BREEDS_ADMIN_PREFIX = '/admin/breeds';
export const BREEDS_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}/breeds`;

// Ads
export const ADMIN_PREFIX = '/admin';
export const ADS_ADMIN_PREFIX = '/admin/ads';
export const ADS_ADMIN_PUPPY_PREFIX = '/admin/ads/puppy';
export const ADS_ADMIN_STUD_PREFIX = '/admin/ads/stud';
export const PUPPY_ADMIN_PREFIX = '/admin/puppy';

// Litters
export const LITTERS_ADMIN_PREFIX = '/admin/litters';

// Sellers
export const SELLER_PREFIX = '/sellers/';
export const PROFILES_ADMIN_PREFIX = '/admin/profiles';
export const PROFILES_ADMIN_CREATION_SUFFIX = '/profiles/create';

// Users
export const USER_ADMIN_PREFIX = '/admin/users';
export const USER_ADMIN_EDIT_SUFFIX = '/edit';

// SellerContacts
export const SELLER_CONTACT_ADMIN_PREFIX = '/admin/seller-contacts';

// Orders
export const ORDER_ADMIN_PREFIX = '/admin/orders';
export const ORDER_ADMIN_VIEW_PREFIX = '/admin/resources/orders';

// Forms
export const FORM_SUBMISSIONS_ADMIN_PREFIX = '/admin/form-submissions';
export const FORM_SUBMISSIONS_SELLER_REVIEWS_ADMIN_PREFIX = `${FORM_SUBMISSIONS_ADMIN_PREFIX}/seller-review`;

export const FORM_SUBMISSIONS_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}/form-submissions`;

export const FORM_SUBMISSIONS_REPORTS_BROWSER_API_PREFIX = `${FORM_SUBMISSIONS_BROWSER_API_PREFIX}/report`;

// Messages
export const MESSAGES_ADMIN_PREFIX = '/admin/all-messages';
export const MESSAGES_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}/messages`;
export const MESSAGE_THREAD_BROWSER_API_PREFIX = `${BROWSER_API_PREFIX}/message-thread`;

// Seller Reviews
export const FORM_SUBMISSIONS_SELLER_REVIEWS_BROWSER_API_PREFIX = `${FORM_SUBMISSIONS_BROWSER_API_PREFIX}/seller-review`;

// Testimonials
export const FORM_SUBMISSIONS_TESTIMONIALS_BROWSER_API_PREFIX = `${FORM_SUBMISSIONS_BROWSER_API_PREFIX}/testimonial`;

/**
 * Google Maps
 */
export const GOOGLE_DEVELOPER_KEY = process.env.MIX_GOOGLE_DEVELOPER_KEY;

/**
 * Breed Constants
 */
export const BREED_SIZE_VALUES = {
  Toy: 'toy',
  Teacup: 'teacup',
  Mini: 'mini',
  Extra: 'extra small',
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
  'Extra Large': 'extra large',
  All: 'all',
} as const;

// pedigree constants
export const BREED_PEDIGREE_TYPES = {
  Designer: 'designer',
  Purebred: 'purebred',
} as const;

export const COAT_TYPES = {
  Silky: 'silky',
  Double: 'double',
  Wire: 'wire',
  Curly: 'curly',
  Smooth: 'smooth',
  Rough: 'rough',
  Wavy: 'wavy',
  Hairless: 'hairless',
  Corded: 'corded',
  All: 'all',
} as const;

export const COAT_LENGTHS = {
  Short: 'short',
  Medium: 'medium',
  Long: 'long',
  All: 'all',
} as const;

export const ENERGY_LEVELS = {
  'Very High': 'very high',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
} as const;

export const SHEDDING_LEVELS = {
  Light: 'light',
  Moderate: 'moderate',
  Heavy: 'heavy',
} as const;

/*
 * Image Constants
 */
export const PUPPY_DEFAULT_IMAGE = '/assets/default_puppy.jpg';
export const BLOG_DEFAULT_IMAGE = '/assets/bg_mingle.png';
export const BREED_DEFAULT_IMAGE = '/assets/bg_mingle.png';
export const LITTER_DEFAULT_IMAGE = '/assets/default_puppy.jpg';
export const BREEDER_SELLER_DEFAULT_IMAGE = '/assets/user-icon-default.svg';

/**
 * Password Rules
 */
export const PASSWORD_MIN_LENGTH = Number(process.env.MIX_PASSWORD_MIN_LENGTH);
export const PASSWORD_MAX_LENGTH = Number(process.env.MIX_PASSWORD_MAX_LENGTH);
export const PASSWORD_INCLUDE_LOWERCASE = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_LOWERCASE);
export const PASSWORD_INCLUDE_UPPERCASE = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_UPPERCASE);
export const PASSWORD_INCLUDE_NUMBERS = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_NUMBERS);
export const PASSWORD_INCLUDE_SYMBOLS = envToBoolean(process.env.MIX_PASSWORD_INCLUDE_SYMBOLS);

/**
 * LP API
 */
export const LP_SITE = process.env.MIX_API_ROOT;
export const LP_API = process.env.MIX_API_BASE_URL || `${LP_SITE}`;

/**
 * App
 */
export const APP_URL = process.env.MIX_APP_URL;

/**
 * BE States
 */
export const BE_STATES = [
  'IN',
  'MI',
  'OH'
];
